"use client";
import { useState, useEffect } from "react";
import Link from "next/link";

/**
 * Cookie consent banner for the landing/marketing pages.
 *
 * Shown to every visitor until they make a choice; the decision is stored in
 * localStorage under CONSENT_KEY so it survives reloads. Three paths:
 *   - "Accept all"       → necessary + analytics + marketing
 *   - "Necessary only"   → necessary cookies only
 *   - "Cookie settings"  → per-category toggles, saved explicitly
 *
 * Styling is inlined to match the landing page design system (DM Sans, white
 * surface, forest-green #2F6B48 brand, sunrise-orange #F97316 accent CTA) and
 * has no external dependencies.
 */

const CONSENT_KEY = "svendetid-cookie-consent";

export type CookieConsentValue = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

export function getStoredConsent(): CookieConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as CookieConsentValue;
  } catch {
    return null;
  }
}

const T = {
  da: {
    title: "Vi bruger cookies",
    body: "Vi bruger nødvendige cookies for at få siden til at fungere. Med dit samtykke bruger vi også cookies til statistik og markedsføring. Læs mere i vores",
    cookiePolicy: "cookiepolitik",
    and: "og",
    terms: "handelsbetingelser",
    acceptAll: "Accepter alle",
    necessaryOnly: "Kun nødvendige",
    settings: "Cookieindstillinger",
    settingsTitle: "Cookieindstillinger",
    save: "Gem valg",
    back: "Tilbage",
    necessary: "Nødvendige",
    necessaryDesc: "Kræves for at siden fungerer (login, sikkerhed). Kan ikke fravælges.",
    analytics: "Statistik",
    analyticsDesc: "Hjælper os med at forstå, hvordan siden bruges, så vi kan forbedre den.",
    marketing: "Markedsføring",
    marketingDesc: "Bruges til at vise relevant indhold og måle effekten af vores markedsføring.",
    alwaysOn: "Altid aktiv",
  },
  en: {
    title: "We use cookies",
    body: "We use necessary cookies to make the site work. With your consent we also use cookies for analytics and marketing. Read more in our",
    cookiePolicy: "cookie policy",
    and: "and",
    terms: "terms of service",
    acceptAll: "Accept all",
    necessaryOnly: "Necessary only",
    settings: "Cookie settings",
    settingsTitle: "Cookie settings",
    save: "Save choices",
    back: "Back",
    necessary: "Necessary",
    necessaryDesc: "Required for the site to work (login, security). Cannot be disabled.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how the site is used so we can improve it.",
    marketing: "Marketing",
    marketingDesc: "Used to show relevant content and measure the effect of our marketing.",
    alwaysOn: "Always on",
  },
} as const;

export default function CookieConsent({ lang = "da" }: { lang?: "da" | "en" }) {
  const t = T[lang];
  const prefix = lang === "en" ? "/en" : "";
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Only decide visibility after mount — localStorage is unavailable during
  // SSR, and reading it in render would cause a hydration mismatch.
  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) setVisible(true);
    else {
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    }
  }, []);

  // Lets the footer's "Cookie settings" link reopen the panel (with the
  // previously saved choices pre-filled) even after consent was given.
  useEffect(() => {
    const reopen = () => {
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  if (!visible) return null;

  const save = (value: Omit<CookieConsentValue, "necessary" | "timestamp">) => {
    const consent: CookieConsentValue = {
      necessary: true,
      ...value,
      timestamp: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    } catch {
      // Storage unavailable (private mode quota etc.) — still dismiss for this visit.
    }
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: consent }));
    setVisible(false);
  };

  const btnBase: React.CSSProperties = {
    fontFamily: "inherit",
    fontSize: 14.5,
    fontWeight: 600,
    padding: "12px 20px",
    borderRadius: 10,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const linkStyle: React.CSSProperties = {
    color: "#16312A",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  };

  const toggleRow = (
    label: string,
    desc: string,
    checked: boolean,
    onChange: (() => void) | null,
  ) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "12px 0", borderTop: "1px solid #DCE3DD" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: "#10211B" }}>{label}</div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#597066", marginTop: 3 }}>{desc}</div>
      </div>
      {onChange ? (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label}
          onClick={onChange}
          style={{
            width: 44,
            height: 24,
            borderRadius: 12,
            border: "none",
            padding: 2,
            cursor: "pointer",
            background: checked ? "#2F6B48" : "#DCE3DD",
            display: "flex",
            justifyContent: checked ? "flex-end" : "flex-start",
            flexShrink: 0,
            transition: "background 0.15s",
          }}
        >
          <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", display: "block" }} />
        </button>
      ) : (
        <span style={{ fontSize: 12, fontWeight: 600, color: "#597066", flexShrink: 0, paddingTop: 3 }}>{t.alwaysOn}</span>
      )}
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t.title}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        padding: "0 16px calc(16px + env(safe-area-inset-bottom))",
        display: "flex",
        justifyContent: "flex-start",
        fontFamily: "'DM Sans', sans-serif",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 720,
          background: "#FFFFFF",
          border: "1px solid #DCE3DD",
          borderRadius: 18,
          padding: "22px 24px",
          boxShadow: "0 18px 48px rgba(16,33,27,0.16)",
          color: "#355247",
        }}
      >
        {!showSettings ? (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "#10211B", margin: "0 0 8px" }}>{t.title}</h2>
            <p style={{ fontSize: 14, lineHeight: 1.6, margin: "0 0 16px" }}>
              {t.body}{" "}
              <Link href={`${prefix}/cookie-policy`} style={linkStyle}>
                {t.cookiePolicy}
              </Link>{" "}
              {t.and}{" "}
              <Link href={`${prefix}/terms`} style={linkStyle}>
                {t.terms}
              </Link>
              .
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => save({ analytics: true, marketing: true })}
                style={{ ...btnBase, background: "#2F6B48", color: "#fff", border: "none", boxShadow: "0 8px 20px rgba(47,107,72,0.28)" }}
              >
                {t.acceptAll}
              </button>
              <button
                type="button"
                onClick={() => save({ analytics: false, marketing: false })}
                style={{ ...btnBase, background: "transparent", color: "#10211B", border: "1px solid #DCE3DD" }}
              >
                {t.necessaryOnly}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                style={{ ...btnBase, background: "transparent", color: "#597066", border: "none", padding: "12px 10px", textDecoration: "underline", textUnderlineOffset: 3 }}
              >
                {t.settings}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "#10211B", margin: "0 0 4px" }}>{t.settingsTitle}</h2>
            {toggleRow(t.necessary, t.necessaryDesc, true, null)}
            {toggleRow(t.analytics, t.analyticsDesc, analytics, () => setAnalytics((v) => !v))}
            {toggleRow(t.marketing, t.marketingDesc, marketing, () => setMarketing((v) => !v))}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => save({ analytics, marketing })}
                style={{ ...btnBase, background: "#2F6B48", color: "#fff", border: "none", boxShadow: "0 8px 20px rgba(47,107,72,0.28)" }}
              >
                {t.save}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                style={{ ...btnBase, background: "transparent", color: "#597066", border: "1px solid #DCE3DD" }}
              >
                {t.back}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
